import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import dayjs from "dayjs";
import MedicationIcon from "@mui/icons-material/Medication";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { getDisplayProduct, getPackTypeDisplay, getSealDetailsDisplay } from "../../../components/utils/utils";
import ConfirmationUnitFields from "./ConfirmationUnitFields";

interface IShoppingCartItemProps {
  item: any;
  availableItems: any;
  setAvailableItems: any;
}

export interface SplitUnit {
  lotNumber: string;
  expDate: string;
  available: boolean;
  editInvalid?: boolean;
}

const chipSx = {
  height: 24,
  fontSize: 13,
  fontWeight: 500,
  color: "text.secondary",
  borderColor: "rgba(15, 23, 42, 0.12)",
  "& .MuiChip-label": { px: 0.75 },
} as const;

const sealedChipSx = {
  ...chipSx,
  color: "success.dark",
  borderColor: "success.light",
  bgcolor: "rgba(46, 125, 50, 0.04)",
} as const;

interface InlinePillProps {
  selected: boolean;
  label: string;
  isAvailable: boolean;
  onClick: () => void;
}

const InlinePill: React.FC<InlinePillProps> = ({ selected, label, isAvailable, onClick }) => (
  <Button
    onClick={onClick}
    disableRipple
    sx={{
      borderRadius: "999px",
      py: 0.25,
      px: 1,
      minHeight: 26,
      fontSize: 13,
      fontWeight: 600,
      lineHeight: 1.2,
      textTransform: "none",
      bgcolor: selected ? "#fff" : "transparent",
      color: selected
        ? isAvailable
          ? "success.dark"
          : "text.primary"
        : "text.secondary",
      boxShadow: selected ? "0 1px 2px rgba(15, 23, 42, 0.08)" : "none",
      "&:hover": { bgcolor: selected ? "#fff" : "rgba(255, 255, 255, 0.5)" },
    }}
  >
    {label}
  </Button>
);

const getUnitPrice = (orderItem: any) => {
  const price = Number(orderItem.price);
  if (!Number.isNaN(price) && price > 0) return price;
  const qty = Number(orderItem.originalQuantity ?? orderItem.quantity) || 1;
  return Number(orderItem.originalTotalAmount ?? orderItem.totalAmount) / qty;
};

export const ActionRequiredItem: React.FunctionComponent<IShoppingCartItemProps> = ({
  item,
  availableItems,
  setAvailableItems,
}) => {
  const currentItem = availableItems.find((i: any) => i.id === item.id) ?? item;
  const displayProduct = getDisplayProduct(currentItem);
  const originalExpMonth = dayjs(displayProduct.expDate).startOf("month");
  const originalLotNumber = displayProduct.lotNumber || "";
  const originalExpDate = dayjs(displayProduct.expDate);
  const originalQuantity = currentItem.originalQuantity ?? currentItem.quantity;
  const unitPrice = getUnitPrice(currentItem);

  const [confirm, setConfirm] = useState<string>(currentItem.status === 1 ? "AVAILABLE" : "OUT_OF_STOCK");
  const [imageUrl, setImageUrl] = useState<string>("");

  const isSplit = Boolean(currentItem.isSplit);
  const splitUnits: SplitUnit[] = currentItem.splitUnits ?? [];
  const editedQuantity = currentItem.editedQuantity ?? originalQuantity;
  const effectiveQuantity = isSplit
    ? splitUnits.filter((unit) => unit.available).length
    : editedQuantity;

  const isItemAvailable = confirm === "AVAILABLE";

  const patchItem = (patch: Record<string, unknown>) => {
    const newItems = [...availableItems];
    const foundIndex = newItems.findIndex((newItem: any) => newItem.id === item.id);
    if (foundIndex === -1) return;
    Object.assign(newItems[foundIndex], patch);
    setAvailableItems(newItems);
  };

  const recalcTotalAmount = (qty: number) => String(qty * unitPrice);

  const ensureOriginalFields = () => {
    if (currentItem.originalQuantity === undefined) {
      patchItem({
        originalQuantity: currentItem.quantity,
        originalTotalAmount: currentItem.totalAmount,
      });
    }
  };

  const savedLotNumber = currentItem.editedLotNumber ?? originalLotNumber;
  const savedExpDate = currentItem.editedExpDate ? dayjs(currentItem.editedExpDate) : originalExpDate;

  const setAvailability = (value: "AVAILABLE" | "OUT_OF_STOCK") => {
    const newItems = [...availableItems];
    const foundIndex = newItems.findIndex((newItem: any) => newItem.id === item.id);
    if (foundIndex === -1) return;

    if (value === "OUT_OF_STOCK") {
      newItems[foundIndex].status = 2;
      newItems[foundIndex].editedLotNumber = undefined;
      newItems[foundIndex].editedExpDate = undefined;
      newItems[foundIndex].editedQuantity = undefined;
      newItems[foundIndex].isSplit = false;
      newItems[foundIndex].splitUnits = undefined;
      newItems[foundIndex].editInvalid = false;
      newItems[foundIndex].totalAmount = newItems[foundIndex].originalTotalAmount ?? newItems[foundIndex].totalAmount;
    } else {
      newItems[foundIndex].status = 1;
    }

    setAvailableItems(newItems);
    setConfirm(value);
  };

  const decreaseQuantity = () => {
    ensureOriginalFields();
    const nextQty = Math.max(1, editedQuantity - 1);
    patchItem({
      editedQuantity: nextQty,
      totalAmount: recalcTotalAmount(nextQty),
    });
  };

  const increaseQuantity = () => {
    ensureOriginalFields();
    const nextQty = Math.min(originalQuantity, editedQuantity + 1);
    patchItem({
      editedQuantity: nextQty === originalQuantity ? undefined : nextQty,
      totalAmount: recalcTotalAmount(nextQty),
    });
  };

  const handleSplitUnits = () => {
    ensureOriginalFields();
    const units: SplitUnit[] = Array.from({ length: originalQuantity }, () => ({
      lotNumber: savedLotNumber,
      expDate: savedExpDate.startOf("month").format("YYYY-MM-DD"),
      available: true,
    }));
    patchItem({
      isSplit: true,
      splitUnits: units,
      editedLotNumber: undefined,
      editedExpDate: undefined,
      editedQuantity: undefined,
      totalAmount: recalcTotalAmount(originalQuantity),
      editInvalid: false,
    });
  };

  const handleUndoSplit = () => {
    patchItem({
      isSplit: false,
      splitUnits: undefined,
      editedQuantity: undefined,
      totalAmount: currentItem.originalTotalAmount ?? currentItem.totalAmount,
      editInvalid: false,
    });
  };

  const updateSplitUnit = (index: number, patch: Partial<SplitUnit>) => {
    const newItems = [...availableItems];
    const foundIndex = newItems.findIndex((newItem: any) => newItem.id === item.id);
    if (foundIndex === -1) return;

    const target = newItems[foundIndex];
    // Read the live unit array off the item object (not the render-time closure)
    // so sequential calls within one handler accumulate instead of clobbering.
    const nextUnits: SplitUnit[] = [...(target.splitUnits ?? [])];
    nextUnits[index] = { ...nextUnits[index], ...patch };
    const availableCount = nextUnits.filter((unit) => unit.available).length;
    const hasInvalid = nextUnits.some((unit) => unit.available && unit.editInvalid);

    target.splitUnits = nextUnits;

    if (availableCount === 0) {
      target.status = 2;
      target.editInvalid = false;
      setAvailableItems(newItems);
      setConfirm("OUT_OF_STOCK");
      return;
    }

    target.status = 1;
    target.totalAmount = recalcTotalAmount(availableCount);
    target.editInvalid = hasInvalid;
    setAvailableItems(newItems);
    setConfirm("AVAILABLE");
  };

  const setSplitUnitAvailability = (index: number, available: boolean) => {
    updateSplitUnit(index, { available, editInvalid: false });
  };

  useEffect(() => {
    if (!item) return;
    const formatedNdc = displayProduct.ndc?.replace(/-/g, "");
    if (!formatedNdc) return;
    const imageUrlApi = `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formatedNdc}`;
    axios.get(imageUrlApi).then((response) => {
      setImageUrl(response.data.imageUrl);
    });
  }, [item]);

  const isSealed = displayProduct.packageCondition === 1;
  const packTypeLabel = getPackTypeDisplay(displayProduct.type, displayProduct.packaging);
  const sealDetails = displayProduct.sealDetails
    ? getSealDetailsDisplay(displayProduct.sealDetails)
    : null;

  const showQuantityStepper = !isSplit && originalQuantity > 1 && isItemAvailable;
  const showSplitButton = !isSplit && originalQuantity > 1 && isItemAvailable;
  const quantityLabel = isSplit
    ? `Shipping ${effectiveQuantity} of ${originalQuantity}`
    : editedQuantity !== originalQuantity
      ? `Quantity: ${editedQuantity} of ${originalQuantity}`
      : `Quantity: ${originalQuantity}`;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        px: 1.25,
        py: 0.875,
        mt: 0.875,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, minWidth: 0 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            bgcolor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {imageUrl ? (
            <Box component="img" src={imageUrl} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <MedicationIcon sx={{ fontSize: 26, color: "text.disabled" }} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                lineHeight: 1.25,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayProduct.drugName}
            </Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 700, flexShrink: 0 }}>
              ${Number(currentItem.totalAmount).toFixed(2)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.3 }}>
            NDC {displayProduct.ndc}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mt: 0.625 }}>
        <Chip label={quantityLabel} size="small" variant="outlined" sx={chipSx} />
        {showQuantityStepper && (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title="Decrease quantity to ship. Buyer is charged only for the confirmed quantity." arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={decreaseQuantity}
                  disabled={editedQuantity <= 1}
                  sx={{ p: 0.25, border: "1px solid rgba(15, 23, 42, 0.12)" }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Increase quantity back up to the original ordered amount. You cannot exceed the quantity the buyer ordered." arrow placement="top">
              <span>
                <IconButton
                  size="small"
                  onClick={increaseQuantity}
                  disabled={editedQuantity >= originalQuantity}
                  sx={{ p: 0.25, border: "1px solid rgba(15, 23, 42, 0.12)" }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
        <Chip label={`Package: ${displayProduct.quantityPerPack}`} size="small" variant="outlined" sx={chipSx} />
        <Chip label={packTypeLabel} size="small" variant="outlined" sx={chipSx} />
        <Tooltip title={sealDetails || (isSealed ? "Sealed" : "Non-Sealed")} arrow placement="top">
          <Chip
            label={isSealed ? "Sealed" : "Non-Sealed"}
            size="small"
            variant="outlined"
            sx={isSealed ? sealedChipSx : chipSx}
          />
        </Tooltip>

        {showSplitButton && (
          <Tooltip
            title="Use when this line has quantity greater than 1 and the physical units have different lot numbers or expiration dates. Split creates one row per unit so you can set lot and expiration for each, or mark individual units out of stock."
            arrow
            placement="top"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={handleSplitUnits}
              sx={{
                ml: { xs: 0, sm: 0.5 },
                minHeight: 24,
                py: 0,
                px: 1,
                fontSize: 12,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Split units
            </Button>
          </Tooltip>
        )}

        {isSplit && (
          <Button
            size="small"
            variant="text"
            onClick={handleUndoSplit}
            sx={{
              minHeight: 24,
              py: 0,
              px: 1,
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
            }}
          >
            Undo split
          </Button>
        )}

        {!isSplit && (
          <Box
            sx={{
              display: "inline-flex",
              ml: { xs: 0, sm: "auto" },
              mt: { xs: 0.25, sm: 0 },
              p: 0.25,
              bgcolor: "grey.100",
              borderRadius: "999px",
              flexShrink: 0,
            }}
          >
            <InlinePill
              selected={confirm === "AVAILABLE"}
              label="Available"
              isAvailable
              onClick={() => setAvailability("AVAILABLE")}
            />
            <InlinePill
              selected={confirm === "OUT_OF_STOCK"}
              label="Out of stock"
              isAvailable={false}
              onClick={() => setAvailability("OUT_OF_STOCK")}
            />
          </Box>
        )}
      </Box>

      {isItemAvailable && (
        <Box
          sx={{
            mt: 0.625,
            pt: 0.625,
            borderTop: "1px solid rgba(15, 23, 42, 0.06)",
          }}
        >
          {!isSplit ? (
            <ConfirmationUnitFields
              lotNumber={savedLotNumber}
              expDate={savedExpDate}
              originalLotNumber={originalLotNumber}
              originalExpMonth={originalExpMonth}
              onLotChange={(lotNumber) => {
                const changed = lotNumber !== originalLotNumber;
                patchItem({
                  editedLotNumber: changed ? lotNumber : undefined,
                  editInvalid: false,
                });
              }}
              onExpChange={(expDate) => {
                const changed = dayjs(expDate).startOf("month").valueOf() !== originalExpMonth.valueOf();
                patchItem({
                  editedExpDate: changed ? expDate : undefined,
                  editInvalid: false,
                });
              }}
              onInvalidChange={(invalid) => patchItem({ editInvalid: invalid })}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {splitUnits.map((unit, index) => (
                <Box
                  key={`${item.id}-unit-${index}`}
                  sx={{
                    p: 1,
                    borderRadius: "8px",
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    bgcolor: "rgba(15, 23, 42, 0.015)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary" }}>
                      Unit {index + 1}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 0.25,
                        bgcolor: "grey.100",
                        borderRadius: "999px",
                      }}
                    >
                      <InlinePill
                        selected={unit.available}
                        label="Available"
                        isAvailable
                        onClick={() => setSplitUnitAvailability(index, true)}
                      />
                      <InlinePill
                        selected={!unit.available}
                        label="Out of stock"
                        isAvailable={false}
                        onClick={() => setSplitUnitAvailability(index, false)}
                      />
                    </Box>
                  </Box>
                  {unit.available && (
                    <ConfirmationUnitFields
                      lotNumber={unit.lotNumber}
                      expDate={dayjs(unit.expDate)}
                      originalLotNumber={originalLotNumber}
                      originalExpMonth={originalExpMonth}
                      onLotChange={(lotNumber) => updateSplitUnit(index, { lotNumber })}
                      onExpChange={(expDate) => updateSplitUnit(index, { expDate })}
                      onInvalidChange={(invalid) => updateSplitUnit(index, { editInvalid: invalid })}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActionRequiredItem;
